import React, { useState, useEffect, useCallback } from 'react';
import { Package, AlertTriangle, TrendingDown, IndianRupee, Edit, Trash2, MoreVertical, Plus, List, LayoutGrid, Filter, Clock, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'react-hot-toast';
import { itemsAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import AddItemModal from '../../components/modals/AddItemModal';
import DeleteConfirmationModal from '../../components/modals/DeleteConfirmationModal';
import BulkPriceUpdateModal from '../../components/modals/BulkPriceUpdateModal';
import BulkTaxUpdateModal from '../../components/modals/BulkTaxUpdateModal';
import BulkStatusUpdateModal from '../../components/modals/BulkStatusUpdateModal';
import ColumnFilter from '../../components/shared/ColumnFilter';
import ListPageHeader from '../../components/shared/ListPageHeader';
import ProductDetailDrawer from '../../components/products/ProductDetailDrawer';
import ProductCardView from '../../components/products/ProductCardView';
import AdjustStockModal from '../../components/products/AdjustStockModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// View Toggle Component (moved outside)
const ViewToggle = ({ viewMode, setViewMode }) => (
  <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="view-toggle">
    <button
      onClick={() => setViewMode('list')}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
        viewMode === 'list' 
          ? 'bg-white text-indigo-700 shadow-sm' 
          : 'text-slate-600 hover:text-slate-800'
      }`}
      data-testid="view-list-btn"
    >
      <List className="w-3.5 h-3.5" />
      List
    </button>
    <button
      onClick={() => setViewMode('detail')}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
        viewMode === 'detail' 
          ? 'bg-white text-indigo-700 shadow-sm' 
          : 'text-slate-600 hover:text-slate-800'
      }`}
      data-testid="view-detail-btn"
    >
      <LayoutGrid className="w-3.5 h-3.5" />
      Detailed
    </button>
  </div>
);

// Detail View Filters Component (moved outside)
const DetailViewFilters = ({
  columnFilters,
  setColumnFilters,
  stockFilter,
  setStockFilter,
  expiryFilter,
  setExpiryFilter,
  categoryFilter,
  setCategoryFilter,
  brandFilter,
  setBrandFilter,
  categories,
  brands,
  detailFilteredItemsCount,
  totalItemsCount
}) => (
  <div className="flex items-center gap-3 flex-wrap px-4 py-3 bg-slate-50 border-b border-slate-200">
    {/* Search */}
    <div className="relative flex-1 max-w-xs">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <input
        type="text"
        placeholder="Search products..."
        value={columnFilters.name}
        onChange={(e) => setColumnFilters({...columnFilters, name: e.target.value})}
        className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
      />
    </div>

    {/* Stock Status Filter */}
    <select
      value={stockFilter}
      onChange={(e) => setStockFilter(e.target.value)}
      className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 bg-white"
      data-testid="stock-filter"
    >
      <option value="all">All Stock</option>
      <option value="low">Low Stock</option>
      <option value="out">Out of Stock</option>
    </select>

    {/* Expiry Status Filter */}
    <select
      value={expiryFilter}
      onChange={(e) => setExpiryFilter(e.target.value)}
      className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 bg-white"
      data-testid="expiry-filter"
    >
      <option value="all">All Expiry</option>
      <option value="expired">Expired</option>
      <option value="expiring-7">Expiring in 7 days</option>
      <option value="expiring-30">Expiring in 30 days</option>
      <option value="no-expiry">No Expiry Date</option>
    </select>

    {/* Category Filter */}
    {categories.length > 0 && (
      <select
        value={categoryFilter}
        onChange={(e) => setCategoryFilter(e.target.value)}
        className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white"
        data-testid="category-filter"
      >
        <option value="">All Categories</option>
        {categories.map(cat => (
          <option key={cat} value={cat}>{cat}</option>
        ))}
      </select>
    )}

    {/* Brand Filter */}
    {brands.length > 0 && (
      <select
        value={brandFilter}
        onChange={(e) => setBrandFilter(e.target.value)}
        className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white"
        data-testid="brand-filter"
      >
        <option value="">All Brands</option>
        {brands.map(brand => (
          <option key={brand} value={brand}>{brand}</option>
        ))}
      </select>
    )}

    {/* Clear Filters */}
    {(stockFilter !== 'all' || expiryFilter !== 'all' || categoryFilter || brandFilter || columnFilters.name) && (
      <button
        onClick={() => {
          setStockFilter('all');
          setExpiryFilter('all');
          setCategoryFilter('');
          setBrandFilter('');
          setColumnFilters({...columnFilters, name: ''});
        }}
        className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
      >
        <X className="w-3.5 h-3.5" />
        Clear
      </button>
    )}

    {/* Results Count */}
    <div className="ml-auto text-xs text-slate-500">
      {detailFilteredItemsCount} of {totalItemsCount} products
    </div>
  </div>
);

const ItemsList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // View mode: 'list' or 'detail'
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('products_view_mode') || 'list';
  });
  
  // Product detail drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerProductId, setDrawerProductId] = useState(null);
  
  // Bulk operations state
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showBulkPriceModal, setShowBulkPriceModal] = useState(false);
  const [showBulkTaxModal, setShowBulkTaxModal] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  
  // Detail view filters
  const [stockFilter, setStockFilter] = useState('all'); // all, low, out
  const [expiryFilter, setExpiryFilter] = useState('all'); // all, expired, expiring, valid
  const [categoryFilter, setCategoryFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  
  // Adjust stock modal
  const [adjustStockModal, setAdjustStockModal] = useState({ open: false, item: null });
  
  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    name: '',
    sku: '',
    category: '',
    item_type: [],
    sales_price: { min: '', max: '' },
    stock: { min: '', max: '' }
  });

  // Get unique categories and brands for filters
  const categories = [...new Set(items.map(item => item.category).filter(Boolean))];
  const brands = [...new Set(items.map(item => item.brand).filter(Boolean))];

  // Apply column filters (for list view)
  const filteredItems = items.filter(item => {
    const matchesName = !columnFilters.name || 
      item.name?.toLowerCase().includes(columnFilters.name.toLowerCase());
    
    const matchesSku = !columnFilters.sku || 
      item.code?.toLowerCase().includes(columnFilters.sku.toLowerCase());
    
    const salesPrice = item.pricing?.sales_price || item.selling_price || 0;
    const matchesPrice = (!columnFilters.sales_price?.min || salesPrice >= parseFloat(columnFilters.sales_price.min)) &&
      (!columnFilters.sales_price?.max || salesPrice <= parseFloat(columnFilters.sales_price.max));
    
    const stock = item.current_stock || 0;
    const matchesStock = (!columnFilters.stock?.min || stock >= parseFloat(columnFilters.stock.min)) &&
      (!columnFilters.stock?.max || stock <= parseFloat(columnFilters.stock.max));
    
    const matchesType = columnFilters.item_type.length === 0 || 
      columnFilters.item_type.includes(item.item_type);
    
    return matchesName && matchesSku && matchesPrice && matchesStock && matchesType;
  });

  // Apply detail view filters
  const detailFilteredItems = filteredItems.filter(item => {
    // Stock filter
    const stock = item.current_stock || 0;
    const reorderLevel = item.reorder_level || item.stock_config?.low_stock_threshold || 10;
    
    if (stockFilter === 'out' && stock !== 0) return false;
    if (stockFilter === 'low' && (stock === 0 || stock > reorderLevel)) return false;
    
    // Expiry filter
    if (expiryFilter !== 'all') {
      if (expiryFilter === 'no-expiry') {
        if (item.expiry_date) return false;
      } else if (!item.expiry_date) {
        return false;
      } else {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const expiryDate = new Date(item.expiry_date);
        const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
        
        if (expiryFilter === 'expired' && daysUntilExpiry >= 0) return false;
        if (expiryFilter === 'expiring-7' && (daysUntilExpiry < 0 || daysUntilExpiry > 7)) return false;
        if (expiryFilter === 'expiring-30' && (daysUntilExpiry < 0 || daysUntilExpiry > 30)) return false;
      }
    }
    
    // Category filter
    if (categoryFilter && item.category !== categoryFilter) return false;
    
    // Brand filter
    if (brandFilter && item.brand !== brandFilter) return false;
    
    return true;
  });

  // Update selectAll state based on current selection
  useEffect(() => {
    if (filteredItems.length === 0) {
      setSelectAll(false);
    } else {
      const allSelected = filteredItems.every(item => selectedItems.includes(item.id));
      setSelectAll(allSelected);
    }
  }, [selectedItems, filteredItems]);

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem('products_view_mode', viewMode);
  }, [viewMode]);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchQuery) params.q = searchQuery;
      if (filterType !== 'all') params.item_type = filterType;
      if (filterStatus !== 'all') params.status = filterStatus;
      const response = await itemsAPI.list(params);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error(getApiErrorMessage(error, 'Failed to load items'));
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterType, filterStatus]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Reconcile selection after items list changes (separated from fetchItems to avoid refetch on every checkbox click)
  useEffect(() => {
    if (selectedItems.length > 0) {
      const currentItemIds = items.map(item => item.id);
      setSelectedItems(prev => prev.filter(id => currentItemIds.includes(id)));
    }
  }, [items]);

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredItems.map(item => item.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const clearSelection = () => {
    setSelectedItems([]);
    setSelectAll(false);
  };

  const handleBulkOperationSuccess = () => {
    clearSelection();
    fetchItems();
  };

  const handleDeleteClick = (item, e) => {
    e?.stopPropagation();
    setDeleteModal({ open: true, item });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.item) return;
    
    setDeleteLoading(true);
    try {
      await itemsAPI.delete(deleteModal.item.id);
      toast.success('Product deleted successfully');
      setDeleteModal({ open: false, item: null });
      fetchItems();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(getApiErrorMessage(error, 'Failed to delete product'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleItemClick = (item) => {
    setDrawerProductId(item.id);
    setDrawerOpen(true);
  };

  const handleEditFromDrawer = (item) => {
    setDrawerOpen(false);
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleDeleteFromDrawer = (item) => {
    setDrawerOpen(false);
    setDeleteModal({ open: true, item });
  };

  const stats = {
    total: filteredItems.length,
    lowStock: filteredItems.filter(item => {
      const threshold = item.stock_config?.low_stock_threshold || item.reorder_level || 10;
      return item.current_stock <= threshold && item.current_stock > 0;
    }).length,
    outOfStock: filteredItems.filter(item => item.current_stock === 0 && item.item_type === 'product').length,
    inventoryValue: filteredItems.reduce((sum, item) => {
      const price = item.pricing?.sales_price || item.selling_price || 0;
      const stock = item.current_stock || 0;
      return sum + (price * stock);
    }, 0),
  };

  const formatCompactCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toFixed(0)}`;
  };

  return (
    <div className="h-full flex flex-col" data-testid="products-page">
      {/* Premium Header */}
      <ListPageHeader
        title="Products"
        stats={[
          { icon: Package, label: 'Total', value: stats.total, color: 'slate' },
          { icon: AlertTriangle, label: 'Low Stock', value: stats.lowStock, color: 'amber' },
          { icon: TrendingDown, label: 'Out of Stock', value: stats.outOfStock, color: 'rose' },
          { icon: IndianRupee, label: 'Inventory Value', value: formatCompactCurrency(stats.inventoryValue), color: 'emerald' },
        ]}
        ctaLabel="Add Item"
        onCtaClick={() => { setSelectedItem(null); setShowModal(true); }}
        extraControls={<ViewToggle viewMode={viewMode} setViewMode={setViewMode} />}
      />

      {/* TABLE AREA */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          
          {/* Detail View Filters (only in detail mode) */}
          {viewMode === 'detail' && (
            <DetailViewFilters
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              stockFilter={stockFilter}
              setStockFilter={setStockFilter}
              expiryFilter={expiryFilter}
              setExpiryFilter={setExpiryFilter}
              categoryFilter={categoryFilter}
              setCategoryFilter={setCategoryFilter}
              brandFilter={brandFilter}
              setBrandFilter={setBrandFilter}
              categories={categories}
              brands={brands}
              detailFilteredItemsCount={detailFilteredItems.length}
              totalItemsCount={items.length}
            />
          )}

          <div className="flex-1 flex flex-col overflow-hidden">
            
            {/* Loading State */}
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-[3px] border-slate-200 border-t-indigo-600 rounded-full animate-spin"></div>
                  <p className="text-[13px] text-slate-500 font-normal">Loading items...</p>
                </div>
              </div>
            ) : viewMode === 'list' ? (
              /* LIST VIEW (Original Table) */
              filteredItems.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center">
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                    <Package className="w-7 h-7 text-slate-400" />
                  </div>
                  <p className="text-[15px] font-medium text-slate-600">No items yet</p>
                  <p className="text-[13px] text-slate-400 mt-1 mb-4">Add your first item to get started</p>
                  <button
                    onClick={() => setShowModal(true)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-[13px] font-medium"
                  >
                    Add Item
                  </button>
                </div>
              ) : (
                <div className="flex-1 flex flex-col overflow-hidden">
                  <div className="flex-1 overflow-y-auto">
                    <table className="tl-table table-fixed w-full">
                      <colgroup>
                        <col style={{ width: '40px' }} />
                        <col style={{ width: '30%' }} />
                        <col style={{ width: '15%' }} />
                        <col style={{ width: '15%' }} />
                        <col style={{ width: '15%' }} />
                        <col style={{ width: '15%' }} />
                        <col style={{ width: '60px' }} />
                      </colgroup>
                      <thead className="sticky top-0 z-10">
                        <tr>
                          <th className="tl-th bg-indigo-50/60 text-indigo-700 border-b border-indigo-100">
                            <input
                              type="checkbox"
                              checked={selectAll}
                              onChange={handleSelectAll}
                              className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                            />
                          </th>
                          <th className="tl-th bg-indigo-50/60 text-indigo-700 border-b border-indigo-100">
                            <div className="flex items-center gap-1">
                              Item Name
                              <ColumnFilter
                                type="text"
                                value={columnFilters.name}
                                onChange={(val) => setColumnFilters({...columnFilters, name: val})}
                                placeholder="Filter name..."
                              />
                            </div>
                          </th>
                          <th className="tl-th bg-indigo-50/60 text-indigo-700 border-b border-indigo-100">
                            <div className="flex items-center gap-1">
                              Item Code
                              <ColumnFilter
                                type="text"
                                value={columnFilters.sku}
                                onChange={(val) => setColumnFilters({...columnFilters, sku: val})}
                                placeholder="Filter code..."
                              />
                            </div>
                          </th>
                          <th className="tl-th bg-indigo-50/60 text-indigo-700 border-b border-indigo-100 tl-th-right">
                            <div className="flex items-center justify-end gap-1">
                              Stock Qty
                              <ColumnFilter
                                type="number"
                                value={columnFilters.stock}
                                onChange={(val) => setColumnFilters({...columnFilters, stock: val})}
                              />
                            </div>
                          </th>
                          <th className="tl-th bg-indigo-50/60 text-indigo-700 border-b border-indigo-100 tl-th-right">
                            <div className="flex items-center justify-end gap-1">
                              Selling Price
                              <ColumnFilter
                                type="number"
                                value={columnFilters.sales_price}
                                onChange={(val) => setColumnFilters({...columnFilters, sales_price: val})}
                              />
                            </div>
                          </th>
                          <th className="tl-th bg-indigo-50/60 text-indigo-700 border-b border-indigo-100 tl-th-right">Purchase Price</th>
                          <th className="tl-th bg-indigo-50/60 text-indigo-700 border-b border-indigo-100"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredItems.map((item, index) => (
                          <tr 
                            key={item.id} 
                            data-testid={`item-row-${item.id}`}
                            className={`tl-tr tl-tr-clickable hover:bg-indigo-50/40 ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                            onClick={() => handleItemClick(item)}
                          >
                            <td className="tl-td" style={{ width: '40px' }} onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedItems.includes(item.id)}
                                onChange={() => handleSelectItem(item.id)}
                                className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                              />
                            </td>
                            <td className="tl-td" style={{ width: '30%' }}>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                                  {item.primary_image_url || item.images?.[0]?.url ? (
                                    <img src={item.primary_image_url || item.images?.[0]?.url} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <span className="text-[12px] font-medium text-indigo-700">
                                      {item.name?.charAt(0)?.toUpperCase() || '?'}
                                    </span>
                                  )}
                                </div>
                                <div className="flex flex-col min-w-0">
                                  <span className="tl-cell-primary truncate">{item.name}</span>
                                  {/* Expiry indicator */}
                                  {item.expiry_date && (() => {
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    const expiryDate = new Date(item.expiry_date);
                                    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
                                    if (daysUntilExpiry < 0) {
                                      return <span className="text-[10px] text-red-600 flex items-center gap-1"><Clock className="w-3 h-3" />Expired</span>;
                                    } else if (daysUntilExpiry <= 30) {
                                      return <span className="text-[10px] text-amber-600 flex items-center gap-1"><Clock className="w-3 h-3" />Expires in {daysUntilExpiry}d</span>;
                                    }
                                    return null;
                                  })()}
                                </div>
                              </div>
                            </td>
                            <td className="tl-td tl-cell-regular" style={{ width: '15%' }}>
                              {item.code || <span className="tl-cell-empty">—</span>}
                            </td>
                            <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '15%' }}>
                              {item.track_inventory !== false && item.item_type === 'product' ? (
                                <span className={`${
                                  item.current_stock === 0 ? 'text-red-600 font-medium' :
                                  item.current_stock <= (item.reorder_level || 10) ? 'text-amber-600 font-medium' :
                                  ''
                                }`}>
                                  {item.current_stock || 0} {item.unit || ''}
                                </span>
                              ) : (
                                <span className="tl-cell-empty">—</span>
                              )}
                            </td>
                            <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '15%' }}>
                              {item.selling_price ? `₹${item.selling_price.toLocaleString('en-IN')}` : <span className="tl-cell-empty">—</span>}
                            </td>
                            <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '15%' }}>
                              {item.purchase_price ? `₹${item.purchase_price.toLocaleString('en-IN')}` : <span className="tl-cell-empty">—</span>}
                            </td>
                            <td className="tl-td" style={{ width: '60px' }} onClick={(e) => e.stopPropagation()}>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors">
                                    <MoreVertical className="w-4 h-4" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => { setSelectedItem(item); setShowModal(true); }}>
                                    <Edit className="w-4 h-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={(e) => handleDeleteClick(item, e)}
                                    className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* TABLE FOOTER */}
                  <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                    <p>
                      Showing <span className="font-medium text-slate-700">{filteredItems.length}</span> items
                    </p>
                    <p>
                      Stock Value: <span className="font-medium text-slate-700">{formatCompactCurrency(stats.inventoryValue)}</span>
                    </p>
                  </div>
                </div>
              )
            ) : (
              /* DETAIL VIEW (Card Grid) */
              <div className="flex-1 overflow-y-auto">
                <ProductCardView
                  items={detailFilteredItems}
                  onItemClick={handleItemClick}
                  onEditItem={(item) => { setSelectedItem(item); setShowModal(true); }}
                  onDeleteItem={(item) => handleDeleteClick(item)}
                  onAdjustStock={(item) => setAdjustStockModal({ open: true, item })}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Product Detail Drawer */}
      <ProductDetailDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        productId={drawerProductId}
        onEdit={handleEditFromDrawer}
        onDelete={handleDeleteFromDrawer}
        onRefresh={fetchItems}
        onAdjustStock={(item) => setAdjustStockModal({ open: true, item })}
      />

      {showModal && (
        <AddItemModal
          item={selectedItem}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); fetchItems(); }}
        />
      )}

      {showBulkPriceModal && (
        <BulkPriceUpdateModal
          selectedItems={selectedItems}
          selectionMode="ids"
          filters={{}}
          onClose={() => setShowBulkPriceModal(false)}
          onSuccess={handleBulkOperationSuccess}
        />
      )}

      {showBulkTaxModal && (
        <BulkTaxUpdateModal
          selectedItems={selectedItems}
          selectionMode="ids"
          filters={{}}
          onClose={() => setShowBulkTaxModal(false)}
          onSuccess={handleBulkOperationSuccess}
        />
      )}

      {showBulkStatusModal && (
        <BulkStatusUpdateModal
          selectedItems={selectedItems}
          selectionMode="ids"
          filters={{}}
          onClose={() => setShowBulkStatusModal(false)}
          onSuccess={handleBulkOperationSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, item: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Product"
        itemName={deleteModal.item?.name || 'this product'}
        itemType="Product"
        loading={deleteLoading}
      />

      {/* Adjust Stock Modal */}
      <AdjustStockModal
        isOpen={adjustStockModal.open}
        item={adjustStockModal.item}
        onClose={() => setAdjustStockModal({ open: false, item: null })}
        onSuccess={fetchItems}
      />
    </div>
  );
};

export default ItemsList;
